import { cached, tracked } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import { action } from '@ember/object';
import { subDays } from 'date-fns/subDays';
import { waitFor } from '@ember/test-waiters';
import BaseController from 'uplisting-frontend/pods/base/controller';
import PropertyModel from 'uplisting-frontend/models/property';
import PropertyTagModel from 'uplisting-frontend/models/property-tag';
import SearchHeaderComponent from 'uplisting-frontend/components/ui/search/header';
import {
  formatDateQuery,
  toggleValue,
  fromPx,
  getCssVariable,
} from 'uplisting-frontend/utils';
import { readWithDefault, CookieKey } from 'uplisting-frontend/utils/cookies';

export enum RowOption {
  // default row
  properties = 'properties',

  rates = 'rates',
  airbnbRates = 'airbnbRates',
  directRates = 'directRates',
  bookingDotComRates = 'bookingDotComRates',
  googleRates = 'googleRates',
  homeAwayRates = 'homeAwayRates',

  mlos = 'mlos',
  closedForArrivals = 'closedForArrivals',
  closedForDepartures = 'closedForDepartures',

  changeovers = 'changeovers',

  multiUnits = 'multiUnits',
}

interface IRowOption {
  id: RowOption;
  text: string;
}

interface IRowGroup {
  group: string;
  rows: IRowOption[];
}

const getterToRow = {
  showMultiUnits: RowOption.multiUnits,
  showChangeovers: RowOption.changeovers,
  showMLOS: RowOption.mlos,
  showClosedForArrivals: RowOption.closedForArrivals,
  showClosedForDepartures: RowOption.closedForDepartures,
  showRates: RowOption.rates,
  showAirbnbRates: RowOption.airbnbRates,
  showDirectRates: RowOption.directRates,
  showBookingDotComRates: RowOption.bookingDotComRates,
  showGoogleRates: RowOption.googleRates,
  showVrboRates: RowOption.homeAwayRates,
};

export default class CalendarController extends BaseController {
  @service cookies!: Services['cookies'];

  @service('calendar/manager')
  calendarManager!: Services['calendar/manager'];

  @service('repositories/property')
  propertyRepository!: Services['repositories/property'];

  queryParams = ['from'];

  @cached @tracked from!: string;

  @cached @tracked isFetchingData = false;
  @cached @tracked selectedRowIds!: RowOption[];
  @cached @tracked searchHeaderComponent!: SearchHeaderComponent;

  @cached
  get selectedItems(): (PropertyModel | PropertyTagModel)[] {
    return this.searchHeaderComponent?.selectedItems ?? [];
  }

  @cached
  get fromAsDate(): Date {
    return new Date(this.from);
  }

  @cached
  get properties(): PropertyModel[] {
    return this.propertyRepository.peekAll();
  }

  @cached
  get ids(): string {
    return this.filteredProperties.map((item) => item.id).join(',');
  }

  @cached
  get rowGroups(): IRowGroup[] {
    const { intl } = this;

    return [
      {
        group: intl.t('calendar.rows.rates.group'),
        rows: [
          {
            id: RowOption.rates,
            text: intl.t('calendar.rows.rates.items.spot_rates'),
          },
          {
            id: RowOption.airbnbRates,
            text: intl.t('calendar.rows.rates.items.airbnb_rates'),
          },
          {
            id: RowOption.directRates,
            text: intl.t('calendar.rows.rates.items.direct_rates'),
          },
          {
            id: RowOption.bookingDotComRates,
            text: intl.t('calendar.rows.rates.items.bdc_rates'),
          },
          {
            id: RowOption.googleRates,
            text: intl.t('calendar.rows.rates.items.google_rates'),
          },
          {
            id: RowOption.homeAwayRates,
            text: intl.t('calendar.rows.rates.items.vrbo_rates'),
          },
        ],
      },

      {
        group: intl.t('calendar.rows.restrictions.group'),
        rows: [
          {
            id: RowOption.mlos,
            text: intl.t('calendar.rows.restrictions.items.mlos'),
          },
          {
            id: RowOption.closedForArrivals,
            text: intl.t(
              'calendar.rows.restrictions.items.closed_for_arrivals',
            ),
          },
          {
            id: RowOption.closedForDepartures,
            text: intl.t(
              'calendar.rows.restrictions.items.closed_for_departures',
            ),
          },
        ],
      },

      {
        group: intl.t('calendar.rows.tasks.group'),
        rows: [
          {
            id: RowOption.changeovers,
            text: intl.t('calendar.rows.tasks.items.changeovers'),
          },
        ],
      },

      {
        group: intl.t('calendar.rows.multi_units.group'),
        rows: [
          {
            id: RowOption.multiUnits,
            text: intl.t('calendar.rows.multi_units.items.multi_units'),
          },
        ],
      },
    ];
  }

  @cached
  get hiddenRowsCount(): number {
    const allRowsCount = this.rowGroups.flatMap((group) => group.rows).length;

    return allRowsCount - this.selectedRowIds.length;
  }

  @cached
  get showRates(): boolean {
    return this.selectedRowIds.includes(RowOption.rates);
  }

  @cached
  get showAirbnbRates(): boolean {
    return this.selectedRowIds.includes(RowOption.airbnbRates);
  }

  @cached
  get showDirectRates(): boolean {
    return this.selectedRowIds.includes(RowOption.directRates);
  }

  @cached
  get showBookingDotComRates(): boolean {
    return this.selectedRowIds.includes(RowOption.bookingDotComRates);
  }

  @cached
  get showGoogleRates(): boolean {
    return this.selectedRowIds.includes(RowOption.googleRates);
  }

  @cached
  get showVrboRates(): boolean {
    return this.selectedRowIds.includes(RowOption.homeAwayRates);
  }

  @cached
  get showMLOS(): boolean {
    return this.selectedRowIds.includes(RowOption.mlos);
  }

  @cached
  get showClosedForArrivals(): boolean {
    return this.selectedRowIds.includes(RowOption.closedForArrivals);
  }

  @cached
  get showClosedForDepartures(): boolean {
    return this.selectedRowIds.includes(RowOption.closedForDepartures);
  }

  @cached
  get showChangeovers(): boolean {
    return this.selectedRowIds.includes(RowOption.changeovers);
  }

  @cached
  get showMultiUnits(): boolean {
    return this.selectedRowIds.includes(RowOption.multiUnits);
  }

  @cached
  get fieldsToShow(): RowOption[] {
    const entries = Object.entries(getterToRow);
    const result: RowOption[] = [];

    for (const [getter, row] of entries) {
      if (this[getter]) {
        result.push(row);
      }
    }

    return result;
  }

  @cached
  get defaultRowHeight(): number {
    const bigCeilHeight = fromPx(getCssVariable('--cell-height-normal'));
    const smallCeilHeight = fromPx(getCssVariable('--cell-height-small'));

    const { selectedRowIds } = this;
    const { length } = selectedRowIds;
    const rowsWithoutMultiUnits = selectedRowIds.includes(RowOption.multiUnits)
      ? length - 1
      : length;

    const smallRowsHeight = rowsWithoutMultiUnits * smallCeilHeight;

    return bigCeilHeight + smallRowsHeight;
  }

  @cached
  get filteredProperties(): PropertyModel[] {
    const { selectedItems } = this;

    if (!selectedItems.length) {
      return this.properties;
    }

    return selectedItems.flatMap((item) => {
      if (item instanceof PropertyTagModel) {
        return item.properties;
      }

      return item;
    });
  }

  @action
  dynamicHeightCalculator(property: PropertyModel): number {
    const cache = this.calendarManager.data.propertyCacheForLookup[property.id];
    const rowHeight = this.defaultRowHeight;

    const bigCeilHeight = fromPx(getCssVariable('--cell-height-normal'));
    let additionalOverlapHeight = 0;

    if (cache) {
      // cover case when we have overlapped events on calendar
      // initial row height is already included in the `defaultRowHeight` getter
      const maxEventsPerRow = Math.max(
        ...Object.values(cache).map((items) => items.length),
        1,
      );

      additionalOverlapHeight = bigCeilHeight * (maxEventsPerRow - 1);
    }

    const height = rowHeight + additionalOverlapHeight;

    if (!this.showMultiUnits || !property.multiUnits?.length) {
      return height;
    }

    return height + property.multiUnits.length * bigCeilHeight;
  }

  @action
  handleInitSearchHeader(component: SearchHeaderComponent): void {
    this.searchHeaderComponent = component;
  }

  @action
  handleSelectRow(value: IRowOption): void {
    toggleValue(this, 'selectedRowIds', value.id);

    this.cookies.write(CookieKey.CALENDAR_ROWS, this.selectedRowIds.join(','));
  }

  @action
  @waitFor
  async handleSelectFrom(date: Date): Promise<void> {
    this.from = formatDateQuery(date);

    await this.handleLoadData();
  }

  @action
  handleGoToToday(): void {
    const date = this.calendarManager.utils.getNavigationDates().today;

    this.handleSelectFrom(date);
  }

  @action
  handleLoadPrevRange(): void {
    const date = this.calendarManager.utils.getNavigationDates(
      this.fromAsDate,
    ).previousRange;

    this.handleSelectFrom(date);
  }

  @action
  handleLoadPrevWeek(): void {
    const date = this.calendarManager.utils.getNavigationDates(
      this.fromAsDate,
    ).previousWeek;

    this.handleSelectFrom(date);
  }

  @action
  handleLoadNextWeek(): void {
    const date = this.calendarManager.utils.getNavigationDates(
      this.fromAsDate,
    ).nextWeek;

    this.handleSelectFrom(date);
  }

  @action
  handleLoadNextRange(): void {
    const date = this.calendarManager.utils.getNavigationDates(
      this.fromAsDate,
    ).nextRange;

    this.handleSelectFrom(date);
  }

  @action
  @waitFor
  async handleLoadData(isInitialRequest = false): Promise<void> {
    this.resetState();

    if (isInitialRequest) {
      this.setDefaultConfig();
    }

    try {
      this.isFetchingData = true;

      await this.calendarManager.loadData({
        startDate: subDays(this.fromAsDate, 1),
        ids: this.ids,
      });
    } finally {
      this.isFetchingData = false;
    }
  }

  public resetState(): void {
    this.calendarManager.data.resetState();
  }

  private setDefaultConfig(): void {
    this.selectedRowIds = readWithDefault(
      this.cookies,
      CookieKey.CALENDAR_ROWS,
      [RowOption.rates, RowOption.airbnbRates, RowOption.mlos].join(','),
    )
      .split(',')
      .filter(Boolean) as RowOption[];
  }
}
